import { VNode } from 'preact';
import { Price } from '../../common';
import { Indexed, isBlank, isNotPresent, isPresent } from '../../utils';
import { IntlContextWithDictionary, numberI18n, NumberI18n } from '../types';
import { marked } from './dateFormatUtils';

export interface Abbreviation {
  divider: number;
  decimals: number;
}

interface Rounder {
  1: number;
  2: number;
  3: number;
  4: number;
}

export interface PriceFormatOptions {
  abbreviate?: boolean;
  placeholder?: string;
  round?: boolean;
  showCode?: boolean;
  showSign?: boolean;
  showSymbol?: boolean;
  showThousandSeparators?: boolean;
  trimWhitespace?: boolean;
  useCalendarOptions?: boolean;
}
interface CalendarPriceFormatOptions extends PriceFormatOptions {
  symbol?: string;
  useCalendarSymbol?: boolean;
}

interface CustomAbbreviationSettings {
  symbol: string;
  settings: { [digits: number]: Abbreviation };
}

interface PriceFormatSettings {
  roundDigits?: keyof Rounder;
  symbol?: string;
  calendarSymbol?: string;
  customAbbrSettings?: CustomAbbreviationSettings;
  calendarOptions?: CalendarPriceFormatOptions;
}

const DEFAULT_ABBR_SETTINGS: Abbreviation = {
  divider: 1000000,
  decimals: 1,
};

const ABBR_SETTINGS: { [digits: number]: Abbreviation } = {
  5: {
    divider: 1000,
    decimals: 1,
  },
  6: {
    divider: 1000,
    decimals: 0,
  },
  7: {
    divider: 1000000,
    decimals: 2,
  },
};

const ABBR_KO_JA_SETTINGS: { [digits: number]: Abbreviation } = {
  5: {
    divider: 10000,
    decimals: 1,
  },
  6: {
    divider: 10000,
    decimals: 1,
  },
  7: {
    divider: 10000,
    decimals: 0,
  },
};

const MAX_ABBR_DIGITS = 7;

export const DEFAULT_SETTINGS: PriceFormatSettings = {
  roundDigits: 1,
};

export const CURRENCY_SPECIFIC: Indexed<PriceFormatSettings> = {
  CNY: {},
  DKK: {
    calendarOptions: {
      symbol: ',-',
      useCalendarSymbol: true,
    },
  },
  EUR: {
    symbol: '€',
  },
  GBP: {
    symbol: '£',
  },
  RUB: {
    symbol: '₽',
  },
  JPY: {
    symbol: '￥',
    roundDigits: 4,
    customAbbrSettings: {
      symbol: '万',
      settings: ABBR_KO_JA_SETTINGS,
    },
    calendarOptions: {
      showSymbol: false,
    },
  },
  KRW: {
    symbol: '₩',
    roundDigits: 3,
    customAbbrSettings: {
      symbol: '만',
      settings: ABBR_KO_JA_SETTINGS,
    },
  },
  NOK: {
    calendarOptions: {
      symbol: ',-',
      useCalendarSymbol: true,
    },
  },
  SEK: {
    calendarOptions: {
      symbol: ':-',
      useCalendarSymbol: true,
    },
  },
  USD: {
    symbol: '$',
  },
};

const rounder: Rounder = {
  1: 1,
  2: 10,
  3: 100,
  4: 1000,
};

export const getCurrencySymbol = (currencyCode: string): string => CURRENCY_SPECIFIC[currencyCode]?.symbol || '';

export const roundAmount = (amount: string, currencyCode: string): string => {
  const { roundDigits }: PriceFormatSettings = { ...DEFAULT_SETTINGS, ...CURRENCY_SPECIFIC[currencyCode] };
  return (Math.ceil(Number(amount) / rounder[roundDigits]) * rounder[roundDigits]).toString();
};

// abbreviate amount and strip zero cents
export const abbreviateAmount = (
  amount: string,
  thousandAbbr: string,
  millionAbbr: string,
  customAbbrSettings: CustomAbbreviationSettings
): { value: string; abbreviated?: boolean } => {
  if (isNotPresent(amount) || amount.length <= 4) {
    return { value: amount, abbreviated: false };
  }

  const basicAbbr: string = amount.length < 7 ? thousandAbbr : millionAbbr;
  const abbreviation: string = isPresent(customAbbrSettings) ? customAbbrSettings.symbol : basicAbbr;

  const { divider, decimals }: Abbreviation = isPresent(customAbbrSettings)
    ? customAbbrSettings.settings[amount.length] || customAbbrSettings.settings[MAX_ABBR_DIGITS]
    : ABBR_SETTINGS[amount.length] || DEFAULT_ABBR_SETTINGS;

  const value = `${(Number(amount) / divider).toFixed(decimals).replace(/\.0{1,3}$/, '')}${abbreviation}`;

  return { value, abbreviated: true };
};

export const formatPrice = (
  price: Price,
  options: PriceFormatOptions,
  context?: IntlContextWithDictionary
): string | VNode<any> => {
  if (isNotPresent(price)) {
    return undefined;
  }
  const { currencyCode } = price;
  const {
    abbreviate,
    round,
    showCode,
    showSign,
    showSymbol,
    showThousandSeparators = true,
    trimWhitespace,
    symbol: calendarSymbol = undefined,
    useCalendarSymbol = false,
  } = {
    ...options,
    ...(options.useCalendarOptions ? CURRENCY_SPECIFIC[currencyCode]?.calendarOptions : undefined),
  };

  const { currencyFormat, decimalSeparator, thousandSeparator, thousandAbbreviation, millionAbbreviation }: NumberI18n =
    numberI18n(context);
  let symbol: string = showSymbol ? getCurrencySymbol(currencyCode) : '';
  const code: string = showCode ? currencyCode : '';
  const sign: string = Number(price.amount) < 0 ? '-' : showSign ? '+' : '';
  const priceFormat: string = trimWhitespace ? currencyFormat.replace(' ', '') : currencyFormat;
  let amount: string = Math.abs(Number(price.amount)).toString();

  if (abbreviate) {
    const { value, abbreviated } = abbreviateAmount(
      amount,
      thousandAbbreviation,
      millionAbbreviation,
      CURRENCY_SPECIFIC[currencyCode]?.customAbbrSettings
    );
    amount = value;
    symbol = abbreviated ? '' : symbol;
  } else {
    if (round) {
      amount = roundAmount(amount, currencyCode);
    }
    // If we want to show the symbol but no symbol exists
    // replace the symbol with the currencyCode e.g. when showing the
    // starting price in the widget's front page
    if (showSymbol && isBlank(symbol)) {
      symbol = currencyCode;
    }
  }
  if (showThousandSeparators) {
    amount = amount.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousandSeparator}`);
  }
  amount = amount.replace('.', decimalSeparator);

  if (useCalendarSymbol) {
    // The symbol represents the exact amount, so it needs to be straight behind the amount
    return `${sign}${amount}${calendarSymbol}`;
  }
  const formattedPrice: string = priceFormat.replace(/1/, amount).replace(/\$/, symbol).replace(/XYZ/, code).trim();

  return context?.intl?.mark ? marked(currencyFormat, formattedPrice) : `${sign}${formattedPrice}`;
};
